import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { RefreshMinor } from '@shopify/polaris-icons'
import { Button } from 'src/components/shared/Button'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { partnerDriverSearchFormValidator } from './PartnerDriverSearchForm.validator'
import type { PartnerDriverSearchFormValues } from './PartnerDriverSearchForm.interface'

/** Matches the hairline treatment MarketsTableToolbar applies to its white buttons. */
const headerButtonCss = tw`border border-solid border-shades-100 hover:border-shades-200 active:border-shades-200`

interface Props {
  initialValues: PartnerDriverSearchFormValues
  canRefresh: boolean
  onSubmit(values: PartnerDriverSearchFormValues): void
  onRefresh(): void
}

const PartnerDriverSearchFormComponent = ({
  initialValues,
  canRefresh,
  onSubmit,
  onRefresh,
}: Props) => {
  const handleSubmit = useCallback(
    (values: PartnerDriverSearchFormValues) => {
      onSubmit(values)
    },
    [onSubmit],
  )

  return (
    <Formik<PartnerDriverSearchFormValues>
      initialValues={initialValues}
      validationSchema={partnerDriverSearchFormValidator}
      // Back/forward changes the URL query, which must reseed the fields.
      enableReinitialize
      onSubmit={handleSubmit}
    >
      <Form
        css={tw`flex flex-row flex-wrap items-start gap-4 rounded-t-lg border-0 border-b border-t border-solid border-shades-100 bg-white px-4 py-3`}
      >
        <div css={tw`w-64`}>
          <FormikTextField<PartnerDriverSearchFormValues>
            name="email"
            label="Email"
            placeholder="Email or part of it"
            inputProps={{ 'data-testid': 'partner-driver-search-email-input' }}
          />
        </div>
        <div css={tw`w-56`}>
          <FormikTextField<PartnerDriverSearchFormValues>
            name="phone"
            label="Phone"
            placeholder="Digits, any format"
            inputProps={{ 'data-testid': 'partner-driver-search-phone-input' }}
          />
        </div>
        <div css={tw`w-80`}>
          <FormikTextField<PartnerDriverSearchFormValues>
            name="partnerDriverId"
            label="Partner Driver ID"
            placeholder="Exact ID"
            inputProps={{ 'data-testid': 'partner-driver-search-id-input' }}
          />
        </div>
        <div css={tw`mt-6`}>
          <Button
            size="small"
            color="dark"
            type="submit"
            css={tw`px-4`}
            data-testid="partner-driver-search-submit-button"
          >
            Find
          </Button>
        </div>
        {/* Refresh is an action rather than a filter, so it sits at the far end. */}
        <div css={tw`ml-auto mt-6`}>
          <Button
            size="small"
            color="white"
            className={headerButtonCss}
            leftIcon={<RefreshMinor width={20} />}
            disabled={!canRefresh}
            onClick={onRefresh}
            data-testid="partner-driver-search-refresh-button"
          >
            Refresh
          </Button>
        </div>
      </Form>
    </Formik>
  )
}

export const PartnerDriverSearchForm = memo(PartnerDriverSearchFormComponent)
